import { Card, Spin, Tooltip } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { useStationObservations } from '../../contexts/useStationObservations';
import { useTopBarStation } from '../../contexts/useTopBarStation';

// Heat Index Risk Levels and Colors
const getHeatIndexColor = (heatIndex: number | null): string => {
  if (heatIndex === null) return '#808080'; // Gray for no data
  if (heatIndex < 27) return '#52c41a'; // Green - Caution
  if (heatIndex < 32) return '#faad14'; // Yellow - Extreme Caution
  if (heatIndex < 41) return '#fa8c16'; // Orange - Danger
  return '#f5222d'; // Red - Extreme Danger
};

const getHeatIndexPercentage = (heatIndex: number | null): number => {
  if (heatIndex === null) return 0;
  // Map Heat Index values to percentage (0-50°C scale)
  const minTemp = 0;
  const maxTemp = 50;
  return Math.min(Math.max(((heatIndex - minTemp) / (maxTemp - minTemp)) * 100, 0), 100);
};

interface HeatIndexProps {
  compact?: boolean;
}

export const HeatIndex: React.FC<HeatIndexProps> = ({ compact = false }) => {
  const { theme } = useTheme();
  const { currentWeather, loading, tioLoading, dataSource } = useStationObservations();
  const { topBarStationId } = useTopBarStation();

  const isLoading = dataSource === 'stations' ? loading : tioLoading;
  const heatIndex = currentWeather.heatIndex;

  const getDisplayHeatIndex = () => {
    if (isLoading) return '...';
    if (heatIndex === null) return '--';
    return heatIndex.toFixed(1);
  };

  const heatIndexColor = getHeatIndexColor(heatIndex);
  const heatIndexPercentage = getHeatIndexPercentage(heatIndex);

  const card = (
    <Card
      size="small"
      style={{
        background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #d9d9d9',
        minWidth: compact ? '70px' : '100px',
        height: compact ? '32px' : '48px',
      }}
      styles={{ body: { padding: compact ? '2px 6px' : '4px 10px', height: '100%' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px', height: '100%' }}>
        <div
          style={{
            width: '4px',
            height: compact ? '18px' : '32px',
            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: '2px',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: `${heatIndexPercentage}%`,
              background: heatIndexColor,
              transition: 'height 0.3s ease, background 0.3s ease',
              borderRadius: '2px',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          {!compact && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: '9px',
                fontWeight: 500,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                lineHeight: '1.2',
                marginBottom: '2px',
              }}
            >
              <span>Heat Index</span>
              {dataSource === 'stations' && (
                <span style={{ opacity: 0.8 }}>{topBarStationId === 'avg' ? '*' : topBarStationId}</span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <div
              style={{
                fontSize: compact ? '12px' : '16px',
                fontWeight: 600,
                color: theme === 'dark' ? 'white' : 'black',
                lineHeight: '1',
              }}
            >
              {getDisplayHeatIndex()}°C
            </div>
            {isLoading && <Spin size="small" />}
          </div>
        </div>
      </div>
    </Card>
  );

  return compact ? <Tooltip title="Heat Index">{card}</Tooltip> : card;
};
